import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Stack,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  defaultDropAnimationSideEffects,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { blue, orange, green, purple } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';

// 一级指标颜色配置 - 支持暗色模式
const getLevel1Colors = (theme: any) => {
  const isDark = theme.palette.mode === 'dark';
  return {
    '决策': { 
      bg: isDark ? blue[900] : blue[50], 
      border: isDark ? blue[700] : blue[200], 
      text: isDark ? blue[100] : blue[900] 
    },
    '产出': { 
      bg: isDark ? green[900] : green[50], 
      border: isDark ? green[700] : green[200], 
      text: isDark ? green[100] : green[900] 
    },
    '过程': { 
      bg: isDark ? orange[900] : orange[50], 
      border: isDark ? orange[700] : orange[200], 
      text: isDark ? orange[100] : orange[900] 
    },
    '效益': { 
      bg: isDark ? purple[900] : purple[50], 
      border: isDark ? purple[700] : purple[200], 
      text: isDark ? purple[100] : purple[900] 
    }
  };
};

// 生成新的编码
const generateNewCodes = (sortedStructure: any[]) => {
  const level1CodeMap = { '决策': 'A', '过程': 'B', '产出': 'C', '效益': 'D' };
  const newCodes: any[] = [];
  
  sortedStructure.forEach((level1Group) => {
    const level1Code = level1CodeMap[level1Group.name as keyof typeof level1CodeMap];
    
    level1Group.level2Groups.forEach((level2Group: any, level2Index: number) => {
      const level2Code = `${level1Code}${level2Index + 1}`;
      
      level2Group.indicators.forEach((indicator: any, level3Index: number) => {
        const level3Code = `${level2Code}${String(level3Index + 1).padStart(2, '0')}`;
        newCodes.push({
          id: indicator.id,
          indicator_code: level3Code
        });
      });
    });
  });
  
  return newCodes;
};

// 构建初始排序结构
const buildInitialStructure = (indicators: any[]) => {
  // 按一级指标分组
  const level1Groups: any = {};
  indicators.forEach(indicator => {
    if (!level1Groups[indicator.level1_name]) {
      level1Groups[indicator.level1_name] = {};
    }
    if (!level1Groups[indicator.level1_name][indicator.level2_name]) {
      level1Groups[indicator.level1_name][indicator.level2_name] = [];
    }
    level1Groups[indicator.level1_name][indicator.level2_name].push(indicator);
  });

  // 转换为排序结构
  return Object.entries(level1Groups).map(([level1Name, level2Groups]: [string, any]) => ({
    id: `level1-${level1Name}`,
    name: level1Name,
    level2Groups: Object.entries(level2Groups).map(([level2Name, indicators]: [string, any]) => ({
      id: `level2-${level1Name}-${level2Name}`,
      name: level2Name,
      indicators: indicators.sort((a: any, b: any) => {
        // 按现有编码排序
        const aCode = a.indicator_code || '';
        const bCode = b.indicator_code || '';
        return aCode.localeCompare(bCode);
      })
    })).sort((a: any, b: any) => {
      // 按第一个三级指标的编码排序
      const aCode = a.indicators[0]?.indicator_code || '';
      const bCode = b.indicators[0]?.indicator_code || '';
      return aCode.localeCompare(bCode);
    })
  })).sort((a: any, b: any) => {
    // 按一级指标固定顺序：决策、过程、产出、效益
    const order = { '决策': 0, '过程': 1, '产出': 2, '效益': 3 };
    return order[a.name as keyof typeof order] - order[b.name as keyof typeof order];
  });
};

// 可排序的二级指标组件（包含所有三级指标）
function SortableLevel2Group({ level2Group, level1Name, level1Colors }: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: level2Group.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 2,
        border: `1px solid ${level1Colors[level1Name as keyof typeof level1Colors]?.border}`,
        borderRadius: 1,
        backgroundColor: 'background.paper',
      }}
    >
      <Paper
        sx={{
          p: 1.5,
          display: 'flex',
          alignItems: 'center',
          cursor: 'grab',
          backgroundColor: level1Colors[level1Name as keyof typeof level1Colors]?.bg,
          '&:active': {
            cursor: 'grabbing',
          },
        }}
        {...attributes}
        {...listeners}
      >
        <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
        <Typography
          variant="subtitle1"
          sx={{
            color: level1Colors[level1Name as keyof typeof level1Colors]?.text,
            fontWeight: 'medium',
          }}
        >
          {level2Group.name}
        </Typography>
      </Paper>
      <Box sx={{ pl: 2, pt: 1 }}>
        <SortableContext
          items={level2Group.indicators.map((ind: any) => ind.id)}
          strategy={verticalListSortingStrategy}
        >
          {level2Group.indicators.map((indicator: any) => (
            <SortableIndicator
              key={indicator.id}
              indicator={indicator}
            />
          ))}
        </SortableContext>
      </Box>
    </Box>
  );
}

// 可排序的指标组件
function SortableIndicator({ indicator }: any) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: indicator.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      sx={{
        p: 1,
        mb: 1,
        display: 'flex',
        alignItems: 'center',
        cursor: 'grab',
        '&:active': {
          cursor: 'grabbing',
        },
      }}
      {...attributes}
      {...listeners}
    >
      <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
      <Box sx={{ flexGrow: 1 }}>
        <Typography variant="body2" fontWeight="bold">
          {indicator.indicator_code}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {indicator.indicator_name}
        </Typography>
      </Box>
    </Paper>
  );
}

interface IndicatorSortDialogProps {
  open: boolean;
  onClose: () => void;
  indicators: any[];
  onSave: (newCodes: any[]) => Promise<void>;
  isSaving?: boolean;
}

const IndicatorSortDialog: React.FC<IndicatorSortDialogProps> = ({ 
  open, 
  onClose, 
  indicators, 
  onSave, 
  isSaving = false 
}) => {
  const theme = useTheme();
  const level1Colors = getLevel1Colors(theme);
  const [sortedStructure, setSortedStructure] = useState<any[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeItem, setActiveItem] = useState<any>(null);
  const [activeType, setActiveType] = useState<'level2' | 'level3' | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 构建初始排序结构
  useEffect(() => {
    if (indicators && indicators.length > 0) {
      const structure = buildInitialStructure(indicators);
      setSortedStructure(structure);
      setHasChanges(false);
    }
  }, [indicators]);

  const handleDragStart = (event: any) => {
    const { active } = event;
    // 判断是二级指标还是三级指标
    const isLevel2 = active.id.toString().startsWith('level2-');
    setActiveType(isLevel2 ? 'level2' : 'level3');
    
    if (isLevel2) {
      // 找到被拖拽的二级指标组
      const level2Group = findLevel2Group(active.id);
      setActiveItem(level2Group);
    } else {
      // 找到被拖拽的三级指标
      const activeItem = findActiveItem(active.id);
      setActiveItem(activeItem);
    }
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      setSortedStructure((items) => {
        const newStructure = [...items];
        
        const isLevel2 = active.id.toString().startsWith('level2-');
        
        if (isLevel2) {
          // 拖动二级指标：移动整个二级指标组及其所有三级指标
          const { sourceLevel1, sourceIndex, targetLevel1, targetIndex } = findLevel2DragPositions(
            newStructure, 
            active.id, 
            over.id
          );
          
          if (sourceLevel1 && targetLevel1 && sourceIndex !== -1 && targetIndex !== -1) {
            // 移动整个二级指标组
            const movedGroup = sourceLevel1.level2Groups.splice(sourceIndex, 1)[0];
            targetLevel1.level2Groups.splice(targetIndex, 0, movedGroup);
            setHasChanges(true);
          }
        } else {
          // 拖动三级指标：只移动单个指标
          const { sourceGroup, sourceIndex, targetGroup, targetIndex } = findDragPositions(
            newStructure, 
            active.id, 
            over.id
          );
          
          if (sourceGroup && targetGroup) {
            // 移动指标
            const movedItem = sourceGroup.indicators.splice(sourceIndex, 1)[0];
            targetGroup.indicators.splice(targetIndex, 0, movedItem);
            setHasChanges(true);
          }
        }
        
        return newStructure;
      });
    }
    
    setActiveItem(null);
    setActiveType(null);
  };

  const findActiveItem = (id: string) => {
    for (const level1Group of sortedStructure) {
      for (const level2Group of level1Group.level2Groups) {
        const indicator = level2Group.indicators.find((ind: any) => ind.id === id);
        if (indicator) return indicator;
      }
    }
    return null;
  };

  const findLevel2Group = (id: string) => {
    for (const level1Group of sortedStructure) {
      const level2Group = level1Group.level2Groups.find((g: any) => g.id === id);
      if (level2Group) return level2Group;
    }
    return null;
  };

  const findDragPositions = (structure: any[], activeId: string, overId: string) => {
    let sourceGroup: any = null;
    let sourceIndex = -1;
    let targetGroup: any = null;
    let targetIndex = -1;

    for (const level1Group of structure) {
      for (const level2Group of level1Group.level2Groups) {
        const activeIndex = level2Group.indicators.findIndex((ind: any) => ind.id === activeId);
        const overIndex = level2Group.indicators.findIndex((ind: any) => ind.id === overId);
        
        if (activeIndex !== -1) {
          sourceGroup = level2Group;
          sourceIndex = activeIndex;
        }
        if (overIndex !== -1) {
          targetGroup = level2Group;
          targetIndex = overIndex;
        }
      }
    }

    return { sourceGroup, sourceIndex, targetGroup, targetIndex };
  };

  const findLevel2DragPositions = (structure: any[], activeId: string, overId: string) => {
    let sourceLevel1: any = null;
    let sourceIndex = -1;
    let targetLevel1: any = null;
    let targetIndex = -1;

    for (const level1Group of structure) {
      const activeIndex = level1Group.level2Groups.findIndex((g: any) => g.id === activeId);
      const overIndex = level1Group.level2Groups.findIndex((g: any) => g.id === overId);
      
      if (activeIndex !== -1) {
        sourceLevel1 = level1Group;
        sourceIndex = activeIndex;
      }
      // 检查 overId 是否是二级指标或三级指标
      if (overIndex !== -1) {
        targetLevel1 = level1Group;
        targetIndex = overIndex;
      } else {
        // 如果 overId 是三级指标，找到它所属的二级指标
        for (const level2Group of level1Group.level2Groups) {
          const indicatorIndex = level2Group.indicators.findIndex((ind: any) => ind.id === overId);
          if (indicatorIndex !== -1) {
            targetLevel1 = level1Group;
            // 找到该三级指标所属的二级指标索引
            targetIndex = level1Group.level2Groups.findIndex((g: any) => g.id === level2Group.id);
            break;
          }
        }
      }
    }

    return { sourceLevel1, sourceIndex, targetLevel1, targetIndex };
  };

  const handleSave = async () => {
    const newCodes = generateNewCodes(sortedStructure);
    await onSave(newCodes);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">指标排序</Typography>
          <Button
            startIcon={<CloseIcon />}
            onClick={onClose}
            disabled={isSaving}
          >
            关闭
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          拖拽指标卡片可以调整指标顺序。排序完成后将自动生成新的指标编码。
        </Alert>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <Stack spacing={3}>
            {sortedStructure.map((level1Group) => (
              <Paper
                key={level1Group.id}
                elevation={1}
                sx={{
                  p: 2,
                  backgroundColor: level1Colors[level1Group.name as keyof typeof level1Colors]?.bg,
                  border: `1px solid ${level1Colors[level1Group.name as keyof typeof level1Colors]?.border}`,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: level1Colors[level1Group.name as keyof typeof level1Colors]?.text,
                    mb: 2,
                    fontWeight: 'bold',
                  }}
                >
                  {level1Group.name}
                </Typography>

                <SortableContext
                  items={[
                    ...level1Group.level2Groups.map((g: any) => g.id),
                    ...level1Group.level2Groups.flatMap((g: any) => g.indicators.map((ind: any) => ind.id))
                  ]}
                  strategy={verticalListSortingStrategy}
                >
                  <Stack spacing={2}>
                    {level1Group.level2Groups.map((level2Group: any) => (
                      <SortableLevel2Group
                        key={level2Group.id}
                        level2Group={level2Group}
                        level1Name={level1Group.name}
                        level1Colors={level1Colors}
                      />
                    ))}
                  </Stack>
                </SortableContext>
              </Paper>
            ))}
          </Stack>

          <DragOverlay
            dropAnimation={{
              sideEffects: defaultDropAnimationSideEffects({
                styles: {
                  active: {
                    opacity: '0.5',
                  },
                },
              }),
            }}
          >
            {activeItem ? (
              activeType === 'level2' ? (
                <Paper
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.8,
                    transform: 'rotate(5deg)',
                    minWidth: 200,
                  }}
                >
                  <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="subtitle1" fontWeight="medium">
                    {activeItem.name}
                  </Typography>
                </Paper>
              ) : (
                <Paper
                  sx={{
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    opacity: 0.8,
                    transform: 'rotate(5deg)',
                  }}
                >
                  <DragIndicatorIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight="bold">
                      {activeItem.indicator_code}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {activeItem.indicator_name}
                    </Typography>
                  </Box>
                </Paper>
              )
            ) : null}
          </DragOverlay>
        </DndContext>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          取消
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={isSaving ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={isSaving || !hasChanges}
        >
          {isSaving ? '保存中...' : '保存排序'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default IndicatorSortDialog;
